import { ListRow, Spinner } from "@salesko/design-system";
import { useQuery } from "@tanstack/react-query";
import { Coins, Lock } from "lucide-react";
import { useI18n } from "../../i18n";
import { getAgentResearchEntitlement, getCreditBalance, getCreditHistory } from "../../lib/api";
import { SettingsNote, SettingsSection } from "./SettingsSection";

/**
 * Agent credit balance + recent ledger for the current tenant. Read-only;
 * top-ups happen on the pricing page. Balance/history come from the credit
 * ledger backed by deploy/sql/0041_billing_credits.sql — but that ledger only
 * means anything for the Business · Managed (metered) tier. Free tenants have
 * no Deep Research access at all, and BYOK tenants run it on their own computer
 * with their own runtime sign-in, so a "0 credits" balance for either of those
 * would be misleading rather than informative.
 */
export function CreditsPanel({ enabled }: { enabled: boolean }) {
  const { t } = useI18n();
  const entitlementQuery = useQuery({
    queryKey: ["agent-research-entitlement"],
    queryFn: getAgentResearchEntitlement,
    enabled,
    retry: 1,
    staleTime: 30_000
  });
  const privateAgentAccessMode = entitlementQuery.data?.entitlement.privateAgentAccessMode ?? "none";

  const balanceQuery = useQuery({
    queryKey: ["billing-credit-balance"],
    queryFn: getCreditBalance,
    enabled: enabled && privateAgentAccessMode === "metered",
    retry: 1,
    staleTime: 15_000
  });
  const historyQuery = useQuery({
    queryKey: ["billing-credit-history"],
    queryFn: getCreditHistory,
    enabled: enabled && privateAgentAccessMode === "metered",
    retry: 1,
    staleTime: 15_000
  });

  const balance = balanceQuery.data?.balance ?? 0;
  const transactions = historyQuery.data?.transactions ?? [];

  return (
    <SettingsSection
      ariaLabel={t.credits.ariaLabel}
      id="settings-credits"
      title={t.credits.title}
      trailing={balanceQuery.isFetching ? <Spinner size={16} /> : <Coins size={15} />}
    >
      {!enabled ? <SettingsNote>{t.credits.signIn}</SettingsNote> : null}

      {enabled && privateAgentAccessMode === "none" ? (
        <SettingsNote>
          <Lock size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
          {t.credits.upgrade}
        </SettingsNote>
      ) : null}

      {enabled && privateAgentAccessMode === "byok" ? <SettingsNote>{t.credits.byok}</SettingsNote> : null}

      {enabled && privateAgentAccessMode === "metered" ? (
        <>
          <p style={{ margin: 0, fontFamily: "var(--font-sans)" }}>
            <strong style={{ fontSize: "var(--text-title-md)", color: "var(--text-strong)" }}>
              {balance.toLocaleString()}
            </strong>{" "}
            <span style={{ color: "var(--text-muted)", fontSize: "var(--text-body-sm)" }}>{t.credits.available}</span>
          </p>

          {transactions.length === 0 ? (
            <SettingsNote>{t.credits.empty}</SettingsNote>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
              {transactions.map((tx) => (
                <li key={tx.transactionNo}>
                  <ListRow
                    subtitle={new Date(tx.createdAt).toLocaleDateString()}
                    title={tx.description ?? tx.scene ?? tx.transactionType}
                    trailing={
                      <span
                        style={{
                          color: tx.credits < 0 ? "var(--red-600)" : "var(--green-600)",
                          fontWeight: "var(--fw-bold)",
                          fontSize: "var(--text-body-sm)"
                        }}
                      >
                        {tx.credits > 0 ? "+" : ""}
                        {tx.credits.toLocaleString()}
                      </span>
                    }
                  />
                </li>
              ))}
            </ul>
          )}

          {balanceQuery.isError ? (
            <p
              role="alert"
              style={{ margin: 0, color: "var(--red-600)", fontSize: "var(--text-body-sm)", fontFamily: "var(--font-sans)" }}
            >
              {t.credits.loadError}
            </p>
          ) : null}
        </>
      ) : null}
    </SettingsSection>
  );
}
